'use client';

import { c as _c } from "react/compiler-runtime";
import { Badge } from '@common/components';
import SvgChevronright from '@common/icon/SvgChevronright';
import { cn } from '@core/utils';
import Image from 'next/image';
import type { ComponentType } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { TERM_CATEGORY } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { DONATION_ROUTES, ROUTES } from '@/constants';
import { useIsOutsideWelloChannel } from '@/hooks/useIsOutsideWelloChannel';
import { useUserAgentMobile } from '@/hooks/useUserAgentMobile';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { useGlobalStore } from '@/stores/GlobalStore';
import { KosafHouseholdShortcutRow, type ShortcutActionRowProps } from './KosafHouseholdShortcutRow';
import { isExternalHref, isReachableHref } from './MenuItems';
type ShortcutAction = 'kosaf-household';

//* 링크 이동이 아닌 클릭 핸들러가 필요한 항목을 매핑. 새 액션 추가 시 이 맵에만 등록.
const SHORTCUT_ACTION_COMPONENTS: Record<ShortcutAction, ComponentType<ShortcutActionRowProps>> = {
  'kosaf-household': KosafHouseholdShortcutRow
};
const ICON_BASE = 'https://cdn.welfarehello.com/static/shortcut';
const BUSINESS_INFO_HREF = 'https://www.ftc.go.kr/bizCommPop.do?wrkr_no=7058602126';
const PARTNERS_HIDDEN_HREFS: readonly string[] = [DONATION_ROUTES.PARTNERS.pathname, ROUTES.BENEFIT_INVITE.pathname];
interface ShortcutItem {
  readonly id: string;
  readonly label: string;
  readonly href: string;
  readonly iconSrc: string;
  readonly termsDrawerType?: TERM_CATEGORY;
  readonly actionType?: ShortcutAction;
  //* 모바일 디바이스(iOS/Android)에서만 노출. 데스크톱 웹에서는 숨김.
  //* 가구원 동의처럼 앱 스킴 리다이렉트가 필요해 데스크톱 의미가 없는 항목에 사용.
  readonly mobileOnly?: boolean;
  readonly isNew?: boolean;
  //* 여백 없이 그려진 아이콘을 다른 아이콘의 시각 크기에 맞출 때 사용.
  readonly iconClassName?: string;
}
interface ShortcutSection {
  readonly id: string;
  readonly title: string;
  readonly shortcutHref?: string;
  readonly items: readonly ShortcutItem[];
}
const SECTIONS: readonly ShortcutSection[] = [{
  id: 'wello',
  title: '웰로',
  shortcutHref: ROUTES.HOME.pathname,
  items: [{
    id: 'custom-policy',
    label: '맞춤정책',
    href: ROUTES.RECOMMEND_POLICY_LIST.pathname,
    iconSrc: `${ICON_BASE}/custom-policy.svg`
  }, {
    id: 'hometown-news',
    label: '동네소식',
    href: ROUTES.SEARCH.withSearchParams({
      searchParams: {
        category: 'hometown-news'
      }
    }),
    iconSrc: `${ICON_BASE}/hometown-news.svg`
  }, {
    id: 'policy-guide',
    label: '아티클',
    href: ROUTES.SEARCH.withSearchParams({
      searchParams: {
        category: 'policy-info'
      }
    }),
    iconSrc: `${ICON_BASE}/policy-guide.svg`
  }, {
    id: 'hospital',
    label: '병원·응급실 찾기',
    href: ROUTES.RECOMMEND_POLICY_SITUATION_EMERGENCY_FORM.pathname,
    iconSrc: `${ICON_BASE}/hospital.svg`
  }, {
    id: 'policy-consult',
    label: '정책상담',
    href: ROUTES.WELLO_AGENT.pathname,
    iconSrc: '/assets/pngs/shortcut/policy-consult.png'
  }]
}, {
  id: 'donation',
  title: '고향사랑기부',
  shortcutHref: DONATION_ROUTES.HOME.pathname,
  items: [{
    id: 'donation-info',
    label: '고향사랑기부 안내',
    href: DONATION_ROUTES.GUIDE.pathname,
    iconSrc: `${ICON_BASE}/notebook.svg`
  }, {
    id: 'gift',
    label: '답례품',
    href: DONATION_ROUTES.GIFTS.pathname,
    iconSrc: `${ICON_BASE}/gift.svg`
  }, {
    id: 'designated',
    label: '지정기부',
    href: DONATION_ROUTES.HOMETOWNS_BUSINESS.pathname,
    iconSrc: `${ICON_BASE}/designated.svg`
  }, {
    id: 'event',
    label: '이벤트',
    href: DONATION_ROUTES.MY_ETC_EVENT.pathname,
    iconSrc: `${ICON_BASE}/event.svg`
  }, {
    id: 'jeju-pass',
    label: '탐나는 제주패스',
    href: DONATION_ROUTES.MY_JEJU_RECOMMEND_INFO.pathname,
    iconSrc: `${ICON_BASE}/donation-info.svg`
  }, {
    id: 'partners',
    label: '웰로 파트너스',
    href: DONATION_ROUTES.PARTNERS.pathname,
    iconSrc: `${ICON_BASE}/partners-v2.svg`,
    isNew: true
  }]
}, {
  id: 'benefits',
  title: '혜택',
  items: [{
    id: 'invite',
    label: '친구초대',
    href: ROUTES.BENEFIT_INVITE.pathname,
    iconSrc: `${ICON_BASE}/invite.svg`,
    iconClassName: 'p-2',
    isNew: true
  }, {
    id: 'wello-point-reward',
    label: '혜택',
    href: ROUTES.BENEFIT.pathname,
    iconSrc: `${ICON_BASE}/gift.svg`
  }, {
    id: 'attendance',
    label: '출석체크',
    href: ROUTES.BENEFIT_ATTENDANCE.pathname,
    iconSrc: `${ICON_BASE}/attendance.svg`
  }, {
    id: 'insurance-refund',
    label: '연말정산 보험료 환급',
    href: 'https://blink.do/Wello',
    iconSrc: `${ICON_BASE}/insurance-refund.svg`
  }]
}, {
  id: 'useful-services',
  title: '유용한 서비스',
  items: [{
    id: 'benefit-notification',
    label: '행정안전부 혜택알리미',
    href: ROUTES.BENEFIT_NOTIFICATION_AGREEMENT.pathname,
    iconSrc: `${ICON_BASE}/benefit-notification.svg`
  }, {
    id: 'scholarship-report',
    label: '한국장학재단 학자금 채무자 신고',
    href: ROUTES.POLICY_CONSENT.pathname,
    iconSrc: `${ICON_BASE}/scholarship-report.svg`
  }, {
    id: 'scholarship-household',
    label: '한국장학재단 가구원 동의',
    href: '#',
    iconSrc: `${ICON_BASE}/scholarship-household.svg`,
    actionType: 'kosaf-household',
    mobileOnly: true
  }]
}, {
  id: 'policy-legal',
  title: '정책 및 법률',
  items: [{
    id: 'terms',
    label: '이용약관',
    href: '#',
    iconSrc: `${ICON_BASE}/note.svg`,
    termsDrawerType: TERM_CATEGORY.SERVICE_TERMS
  }, {
    id: 'user-info',
    label: '사용자정보확인',
    href: BUSINESS_INFO_HREF,
    iconSrc: `${ICON_BASE}/note.svg`
  }, {
    id: 'privacy',
    label: '개인정보처리방침',
    href: '#',
    iconSrc: `${ICON_BASE}/note.svg`,
    termsDrawerType: TERM_CATEGORY.PRIVACY_POLICY
  }, {
    id: 'marketing',
    label: '마케팅이용동의',
    href: '#',
    iconSrc: `${ICON_BASE}/note.svg`,
    termsDrawerType: TERM_CATEGORY.MARKETING_TERMS
  }, {
    id: 'location-terms',
    label: '위치기반서비스 이용약관',
    href: '#',
    iconSrc: `${ICON_BASE}/note.svg`,
    termsDrawerType: TERM_CATEGORY.LOCATION_POLICY_SELECT
  }]
}, {
  id: 'customer-service',
  title: '고객센터',
  items: [{
    id: 'notice',
    label: '공지사항',
    href: ROUTES.MY_NOTICE.pathname,
    iconSrc: `${ICON_BASE}/notice.svg`
  }, {
    id: 'faq',
    label: 'FAQ',
    href: ROUTES.MY_FAQ.pathname,
    iconSrc: `${ICON_BASE}/faq.svg`
  }]
}];
const ShortcutButton = t0 => {
  const $ = _c(7);
  const {
    href,
    onNavigate
  } = t0;
  const isExternal = isExternalHref(href);
  const t1 = isExternal ? "_blank" : undefined;
  const t2 = isExternal ? "noopener noreferrer" : undefined;
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="font-600 text-Gray-900 typo-caption2">바로가기</span>;
    t4 = <SvgChevronright className="size-10 text-Gray-700" />;
    $[0] = t3;
    $[1] = t4;
  } else {
    t3 = $[0];
    t4 = $[1];
  }
  let t5;
  if ($[2] !== href || $[3] !== onNavigate || $[4] !== t1 || $[5] !== t2) {
    t5 = <CustomLink href={href} target={t1} rel={t2} onClick={onNavigate} className="flex w-60 items-center justify-center gap-4 rounded-xxsmall bg-Gray-100 py-4 transition-colors hover:bg-Gray-200">{t3}{t4}</CustomLink>;
    $[2] = href;
    $[3] = onNavigate;
    $[4] = t1;
    $[5] = t2;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
};
const ShortcutRow = t0 => {
  const $ = _c(40);
  const {
    item,
    onNavigate,
    termsDrawerMode,
    closeOnTermsOpen: t1
  } = t0;
  const closeOnTermsOpen = t1 === undefined ? true : t1;
  const [setTermsDrawerType, setTermsDrawerMode] = useGlobalStore(useShallow(_temp));
  const isExternal = isExternalHref(item.href);
  let t2;
  if ($[0] !== closeOnTermsOpen || $[1] !== item.termsDrawerType || $[2] !== onNavigate || $[3] !== setTermsDrawerMode || $[4] !== setTermsDrawerType || $[5] !== termsDrawerMode) {
    t2 = () => {
      if (!item.termsDrawerType) {
        return;
      }
      setTermsDrawerMode(termsDrawerMode ?? "default");
      setTermsDrawerType(item.termsDrawerType);
      if (closeOnTermsOpen) {
        onNavigate?.();
      }
    };
    $[0] = closeOnTermsOpen;
    $[1] = item.termsDrawerType;
    $[2] = onNavigate;
    $[3] = setTermsDrawerMode;
    $[4] = setTermsDrawerType;
    $[5] = termsDrawerMode;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleTermsClick = t2;
  if (item.actionType) {
    const ActionRow = SHORTCUT_ACTION_COMPONENTS[item.actionType];
    let t3;
    if ($[7] !== ActionRow || $[8] !== item.iconSrc || $[9] !== item.label || $[10] !== onNavigate) {
      t3 = <ActionRow label={item.label} iconSrc={item.iconSrc} onNavigate={onNavigate} />;
      $[7] = ActionRow;
      $[8] = item.iconSrc;
      $[9] = item.label;
      $[10] = onNavigate;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    return t3;
  }
  if (item.termsDrawerType) {
    const t3 = item.iconSrc;
    let t4;
    if ($[12] !== item.iconClassName) {
      t4 = cn("size-24 shrink-0", item.iconClassName);
      $[12] = item.iconClassName;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    let t5;
    if ($[14] !== item.iconSrc || $[15] !== t4) {
      t5 = <Image src={t3} alt="" width={24} height={24} unoptimized={true} className={t4} />;
      $[14] = item.iconSrc;
      $[15] = t4;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    let t6;
    if ($[17] !== item.label) {
      t6 = <span className="line-clamp-1 font-500 text-Gray-900 typo-body3">{item.label}</span>;
      $[17] = item.label;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    let t7;
    if ($[19] !== handleTermsClick || $[20] !== t5 || $[21] !== t6) {
      t7 = <button type="button" onClick={handleTermsClick} className="flex w-full items-center gap-8 px-20 py-12 text-left transition-colors hover:bg-Gray-50">{t5}{t6}</button>;
      $[19] = handleTermsClick;
      $[20] = t5;
      $[21] = t6;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    return t7;
  }
  const t3 = item.href;
  const t4 = isExternal ? "_blank" : undefined;
  const t5 = isExternal ? "noopener noreferrer" : undefined;
  const t6 = item.iconSrc;
  let t7;
  if ($[23] !== item.iconClassName) {
    t7 = cn("size-24 shrink-0", item.iconClassName);
    $[23] = item.iconClassName;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== item.iconSrc || $[26] !== t7) {
    t8 = <Image src={t6} alt="" width={24} height={24} unoptimized={true} className={t7} />;
    $[25] = item.iconSrc;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== item.label) {
    t9 = <span className="line-clamp-1 font-500 text-Gray-900 typo-body3">{item.label}</span>;
    $[28] = item.label;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== item.isNew) {
    t10 = item.isNew ? <Badge dimensions="H20" className="shrink-0 bg-B-500 px-4 text-Gray-white">NEW</Badge> : null;
    $[30] = item.isNew;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== item.href || $[33] !== onNavigate || $[34] !== t10 || $[35] !== t4 || $[36] !== t5 || $[37] !== t8 || $[38] !== t9) {
    t11 = <CustomLink href={t3} target={t4} rel={t5} onClick={onNavigate} className="flex items-center gap-8 px-20 py-12 transition-colors hover:bg-Gray-50">{t8}{t9}{t10}</CustomLink>;
    $[32] = item.href;
    $[33] = onNavigate;
    $[34] = t10;
    $[35] = t4;
    $[36] = t5;
    $[37] = t8;
    $[38] = t9;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  return t11;
};
export const ServiceShortcutPopoverContent = t0 => {
  const $ = _c(31);
  const {
    onNavigate,
    className,
    contentClassName,
    termsDrawerMode,
    closeOnTermsOpen: t1
  } = t0;
  const closeOnTermsOpen = t1 === undefined ? true : t1;
  const subDomain = useDeviceInfoStore(_temp2);
  const isMobile = useUserAgentMobile();
  const isPartnersHidden = useIsOutsideWelloChannel();
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== className || $[1] !== closeOnTermsOpen || $[2] !== contentClassName || $[3] !== isMobile || $[4] !== isPartnersHidden || $[5] !== onNavigate || $[6] !== subDomain || $[7] !== termsDrawerMode) {
    let t6;
    if ($[12] !== isPartnersHidden || $[13] !== subDomain) {
      t6 = section => ({
        ...section,
        items: section.items.filter(item => isReachableHref(item.href, subDomain) && (!isPartnersHidden || !PARTNERS_HIDDEN_HREFS.includes(item.href)))
      });
      $[12] = isPartnersHidden;
      $[13] = subDomain;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    const sections = SECTIONS.map(t6).filter(_temp3);
    t4 = "\uC11C\uBE44\uC2A4 \uBC14\uB85C\uAC00\uAE30";
    if ($[15] !== className) {
      t5 = cn("py-32 duration-300 animate-in fade-in", className);
      $[15] = className;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    if ($[17] !== contentClassName) {
      t2 = cn("flex flex-col gap-28 px-20 pt-32", contentClassName);
      $[17] = contentClassName;
      $[18] = t2;
    } else {
      t2 = $[18];
    }
    let t7;
    if ($[19] !== closeOnTermsOpen || $[20] !== isMobile || $[21] !== onNavigate || $[22] !== termsDrawerMode) {
      t7 = section_1 => <section key={section_1.id} className="flex flex-col gap-12">{section_1.shortcutHref ? <div className="flex items-center justify-between px-20"><h3 className="flex h-26 items-center font-700 text-Gray-900 typo-body3">{section_1.title}</h3><ShortcutButton href={section_1.shortcutHref} onNavigate={onNavigate} /></div> : <h3 className="flex h-26 items-center px-20 font-700 text-Gray-900 typo-body3">{section_1.title}</h3>}<ul className="flex flex-col">{section_1.items.filter(item_0 => !item_0.mobileOnly || isMobile).map(item_1 => <li key={item_1.id}><ShortcutRow item={item_1} onNavigate={onNavigate} termsDrawerMode={termsDrawerMode} closeOnTermsOpen={closeOnTermsOpen} /></li>)}</ul></section>;
      $[19] = closeOnTermsOpen;
      $[20] = isMobile;
      $[21] = onNavigate;
      $[22] = termsDrawerMode;
      $[23] = t7;
    } else {
      t7 = $[23];
    }
    t3 = sections.map(t7);
    $[0] = className;
    $[1] = closeOnTermsOpen;
    $[2] = contentClassName;
    $[3] = isMobile;
    $[4] = isPartnersHidden;
    $[5] = onNavigate;
    $[6] = subDomain;
    $[7] = termsDrawerMode;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
  }
  let t6;
  if ($[24] !== t2 || $[25] !== t3) {
    t6 = <div className={t2}>{t3}</div>;
    $[24] = t2;
    $[25] = t3;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] !== t4 || $[28] !== t5 || $[29] !== t6) {
    t7 = <nav aria-label={t4} className={t5}>{t6}</nav>;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  return t7;
};
function _temp(state) {
  return [state.setTermsDrawerType, state.setTermsDrawerMode] as const;
}
function _temp2(state) {
  return state.subDomain;
}
function _temp3(section_0) {
  return section_0.items.length > 0;
}